import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { Textarea } from '@/components/ui/textarea';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { ITEM_TYPES } from 'constants/itemType';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  NotificationPriority,
  NotificationType,
  useCreateNotification,
} from 'hooks/useNotification';
import { buildResourceRequestOpenAppMessage } from 'components/MapPage/CorporateViewTabs/NotificationsTab/appMessageTaskLinks';

type Ics213RrRejectDialogProps = {
  open: boolean;
  requestId: string;
  requestLabel: string;
  slotLabel: string;
  defaultRequesterUserId?: string | null;
  onOpenChange: (open: boolean) => void;
  /** Create the new tip with this role removed before notify. */
  onConfirmDraft: () => void | Promise<void>;
  confirmPending?: boolean;
};

/**
 * Confirm reject of one 213RR signature box: new tip without that role, then
 * notify chosen recipients with a typed message.
 */
export function Ics213RrRejectDialog({
  open,
  requestId,
  requestLabel,
  slotLabel,
  defaultRequesterUserId,
  onOpenChange,
  onConfirmDraft,
  confirmPending = false,
}: Ics213RrRejectDialogProps) {
  const incidentId = useActualIncidentId();
  const { data: roster = [] } = useIncidentRoster();
  const { data: positions = [] } = useGetPositionsForIncident();
  const notify = useCreateNotification();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setPositionIds([]);
    const requester = (defaultRequesterUserId ?? '').trim();
    setUserIds(requester ? [requester] : []);
  }, [open, defaultRequesterUserId]);

  const userOptions = useMemo(
    () =>
      roster
        .filter((member) => member.active !== 0 && member.userId)
        .map((member) => ({
          value: member.userId,
          label: member.userName || member.emailAddress || member.userId,
        })),
    [roster]
  );

  const positionOptions = useMemo(
    () =>
      positions
        .filter((position) => !position.isResourcePosition)
        .map((position) => ({
          value: position.id,
          label: position.name,
        })),
    [positions]
  );

  const send = async () => {
    const trimmed = message.trim();
    const fromPositions = roster
      .filter((member) =>
        member.incidentPositions.some((position) =>
          positionIds.includes(position.positionId)
        )
      )
      .map((member) => member.userId);
    const recipients = [...new Set([...userIds, ...fromPositions])].filter(
      Boolean
    );
    if (!incidentId || !trimmed || recipients.length === 0) return;

    await onConfirmDraft();
    const path = `/iap/${incidentId}?openTab=Resources&openRequest=${requestId}`;
    await notify.mutateAsync({
      incidentId,
      acknowledgementRequired: false,
      autoArchiveDate: null,
      priority: NotificationPriority.INFO,
      emailSubject: null,
      emailMessage: null,
      smsMessage: null,
      teamsMessage: null,
      phoneMessage: null,
      appMessage: buildResourceRequestOpenAppMessage({
        prefix: `${trimmed}\n\nRejected ${slotLabel}.`,
        itemName: requestLabel,
        path,
      }),
      userIds: recipients,
      teamIds: [],
      sourceItemId: requestId,
      sourceItemType: ITEM_TYPES.ResourceRequest,
      scheduledDate: null,
      notificationTypes: [NotificationType.IN_APP],
      aors: [],
    });
    setUserIds([]);
    setPositionIds([]);
    setMessage('');
    onOpenChange(false);
  };

  const pending = notify.isPending || confirmPending;
  const canSend =
    message.trim().length > 0 && (userIds.length > 0 || positionIds.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject — {slotLabel}</DialogTitle>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            Rejecting notifies the selected people. When a prior approval exists,
            a new draft tip is created with that last stamp removed so approval
            moves back one step.
          </p>
          <div>
            <Label htmlFor="ics213rr-reject-message" className="tw:mb-1">
              Message
            </Label>
            <Textarea
              id="ics213rr-reject-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Explain why this approval was rejected…"
              rows={4}
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Users
            </div>
            <MultiIdCombobox
              options={userOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder="Select users…"
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Positions
            </div>
            <MultiIdCombobox
              options={positionOptions}
              value={positionIds}
              onChange={setPositionIds}
              placeholder="Select positions…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending || !canSend}
            onClick={() => void send()}
          >
            Reject and create Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
